import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Button,
  Col,
  DatePicker,
  Empty,
  Radio,
  Row,
  Space,
  Switch,
  Table,
  Typography,
} from "antd";
import { DownloadOutlined, ProjectOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import moment, { Moment } from "moment";
import { Client } from "../../core/api-client";
import { API_POST_ALLOCATION_REPORT } from "../../shared/routes/private.routes";
import { RootState } from "../../shared/types/common";
import {
  accessAllowed,
  decryptCTC,
  formatAmount,
  formatDate,
  showNotification,
} from "../../shared/utils/utils";
import { allPermissions } from "../../shared/constants/constants";
import Papa from "papaparse";

type Emp = {
  staffId: string;
  entityName?: string | null;
  employeeTypeName?: string | null;
  employeeId: string;
  employmentModeName?: string | null;
  staffName: string;
  staffStatusId: string;
  doj: string | null;
  lwd: string | null;
  lineManagerName?: string | null;
};

/** Table row: either one row per staff (matrix) or one row per staff×allocation (flat). */
type ReportRow = Emp & {
  rowKey: string;
  allocationClient?: string;
  allocationProjectId?: string;
  allocationProjectLabel?: string;
  allocationPercent?: number;
};

type SalarySnap = {
  id: string;
  startDate: string;
  end_date: string | null;
  updated_date: string;
  encrypted_ctc: string | null;
  iv: string | null;
  salt: string | null;
  ctc: string | null;
  currencyCode: string | null;
  currencyName: string | null;
};

type ReportPayload = {
  month: string;
  employees: Emp[];
  clientColumnKeys: string[];
  projectColumnDefs: { projectId: string; label: string }[];
  clientMatrix: Record<string, Record<string, number>>;
  projectMatrix: Record<string, Record<string, number>>;
  salariesByStaff?: Record<string, SalarySnap[]>;
};

/** Latest band’s monthly CTC (annual÷12); currency from band with highest monthly CTC. */
type StaffSalaryCore = { currency: string; monthlyAmount: number };

function formatScaledCostRd(monthlyAmount: number, pct: number): string {
  const p = Math.max(0, Number(pct) || 0);
  if (!(monthlyAmount > 0) || !(p > 0)) return "";
  const scaled = Math.round(monthlyAmount * (p / 100) * 100) / 100;
  return formatAmount(String(scaled));
}

/** Matrix / API cells may arrive as strings; avoid `0 + "50"` string concat in sums. */
function pctFromMatrixCell(v: unknown): number {
  if (v == null || v === "") return 0;
  if (typeof v === "number" && Number.isFinite(v)) return v;
  const n = Number(String(v).replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : 0;
}

/** Decrypted or raw CTC is annual; normalize decrypt failures (`NA`) and commas. */
function parseAnnualCtc(input: unknown): number {
  if (input == null) return 0;
  const s = String(input).trim();
  if (!s || /^na$/i.test(s)) return 0;
  const n = Number(s.replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
}

async function decryptSalaryCtc(row: SalarySnap): Promise<number> {
  if (row.encrypted_ctc && row.iv && row.salt) {
    try {
      const raw = await decryptCTC(
        moment(row.updated_date).format("YYYY-MM-DD"),
        row.encrypted_ctc,
        row.iv,
        row.salt
      );
      const annual = parseAnnualCtc(raw);
      return annual > 0 ? Math.round((annual / 12) * 100) / 100 : 0;
    } catch {
      return 0;
    }
  }
  const annual = parseAnnualCtc(row.ctc);
  return annual > 0 ? Math.round((annual / 12) * 100) / 100 : 0;
}

/** Currency from band with highest monthly CTC; monthly amount from latest revision (same rules as before). */
async function pickCurrencyAndMonthly(
  rows: SalarySnap[]
): Promise<StaffSalaryCore> {
  if (!rows.length) return { currency: "", monthlyAmount: 0 };
  const withCtc = await Promise.all(
    rows.map(async (row) => ({
      row,
      ctc: await decryptSalaryCtc(row),
    }))
  );
  let maxEntry = withCtc[0];
  for (const e of withCtc) {
    if (e.ctc > maxEntry.ctc) maxEntry = e;
  }
  const currency =
    maxEntry.row.currencyName?.trim() ||
    maxEntry.row.currencyCode?.trim() ||
    "";

  const latest = [...rows].sort((a, b) => {
    const tb = new Date(b.updated_date).getTime();
    const ta = new Date(a.updated_date).getTime();
    if (tb !== ta) return tb - ta;
    return String(b.startDate).localeCompare(String(a.startDate));
  })[0];
  const monthlyAmount = await decryptSalaryCtc(latest);

  return { currency, monthlyAmount };
}

async function buildSalaryCoreMap(
  report: ReportPayload
): Promise<Record<string, StaffSalaryCore>> {
  const map: Record<string, StaffSalaryCore> = {};
  const byStaff = report.salariesByStaff ?? {};
  for (const emp of report.employees) {
    map[emp.staffId] = await pickCurrencyAndMonthly(byStaff[emp.staffId] ?? []);
  }
  return map;
}

function computeRowTotal(
  row: Emp,
  report: ReportPayload,
  mode: "client" | "project"
): number {
  let sum = 0;
  if (mode === "client") {
    const byClient = report.clientMatrix[row.staffId] ?? {};
    for (const key of report.clientColumnKeys) {
      const v = pctFromMatrixCell(byClient[key]);
      if (v > 0) sum += v;
    }
  } else {
    const byProject = report.projectMatrix[row.staffId] ?? {};
    for (const p of report.projectColumnDefs) {
      const pid = String(p.projectId);
      const v = pctFromMatrixCell(
        byProject[p.projectId] ?? byProject[pid]
      );
      if (v > 0) sum += v;
    }
  }
  return Math.round(sum * 100) / 100;
}

/** One row per staff×client or staff×project with allocation &gt; 0; one placeholder row if staff has no allocation. */
function buildExpandedRows(
  report: ReportPayload,
  mode: "client" | "project"
): ReportRow[] {
  const rows: ReportRow[] = [];
  for (const emp of report.employees) {
    if (mode === "client") {
      let any = false;
      for (const clientName of report.clientColumnKeys) {
        const v = pctFromMatrixCell(
          report.clientMatrix[emp.staffId]?.[clientName]
        );
        if (v > 0) {
          any = true;
          rows.push({
            ...emp,
            rowKey: `${emp.staffId}::client::${clientName}`,
            allocationClient: clientName,
            allocationPercent: Math.round(v * 100) / 100,
          });
        }
      }
      if (!any) {
        rows.push({
          ...emp,
          rowKey: `${emp.staffId}::client::__none__`,
          allocationClient: "",
          allocationPercent: undefined,
        });
      }
    } else {
      let any = false;
      const byProject = report.projectMatrix[emp.staffId] ?? {};
      for (const p of report.projectColumnDefs) {
        const v = pctFromMatrixCell(
          byProject[p.projectId] ?? byProject[String(p.projectId)]
        );
        if (v > 0) {
          any = true;
          rows.push({
            ...emp,
            rowKey: `${emp.staffId}::project::${p.projectId}`,
            allocationProjectId: String(p.projectId),
            allocationProjectLabel: p.label,
            allocationPercent: Math.round(v * 100) / 100,
          });
        }
      }
      if (!any) {
        rows.push({
          ...emp,
          rowKey: `${emp.staffId}::project::__none__`,
          allocationProjectLabel: "",
          allocationPercent: undefined,
        });
      }
    }
  }
  return rows;
}

const AllocationReportPage: React.FC = () => {
  const { myPermissions } = useSelector((state: RootState) => state.auth);
  const canView = accessAllowed(myPermissions, allPermissions.VIEW_PROJECT);

  const [month, setMonth] = useState<Moment | null>(null);
  const [mode, setMode] = useState<"client" | "project">("client");
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<ReportPayload | null>(null);
  const [salaryCore, setSalaryCore] = useState<
    Record<string, StaffSalaryCore>
  >({});
  /** False while decrypting salary rows for Currency / Cost_RD (after report is set). */
  const [salaryCoreReady, setSalaryCoreReady] = useState(true);
  /** When true: one row per staff with matrix columns (default off in UI = expanded rows). */
  const [aggregateByStaff, setAggregateByStaff] = useState(false);
  /**
   * Only used when aggregateByStaff is false. When false (default): separate column per
   * client/project. When true: single Client/Project + Allocation % columns.
   */
  const [compactDetailClientProject, setCompactDetailClientProject] =
    useState(false);

  const loadReport = useCallback(async () => {
    if (!month) {
      showNotification("Select a month first", "warning");
      return;
    }
    const ym = month.format("YYYY-MM");
    setLoading(true);
    setReport(null);
    setSalaryCore({});
    setSalaryCoreReady(true);
    try {
      const res = await Client.post(API_POST_ALLOCATION_REPORT, { month: ym });
      const data = res?.data;
      if (data?.employees) {
        setReport(data as ReportPayload);
      } else {
        setReport({
          month: ym,
          employees: [],
          clientColumnKeys: [],
          projectColumnDefs: [],
          clientMatrix: {},
          projectMatrix: {},
          salariesByStaff: {},
        });
      }
    } catch {
      showNotification("Failed to load allocation report", "error");
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    if (!report?.employees?.length) {
      setSalaryCore({});
      setSalaryCoreReady(true);
      return;
    }
    setSalaryCoreReady(false);
    let cancelled = false;
    (async () => {
      try {
        const core = await buildSalaryCoreMap(report);
        if (!cancelled) setSalaryCore(core);
      } catch {
        if (!cancelled) setSalaryCore({});
      } finally {
        if (!cancelled) setSalaryCoreReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [report]);

  const downloadCsv = useCallback(() => {
    if (!report?.employees.length) return;
    const ym = report.month || (month?.format("YYYY-MM") ?? "report");
    const trailingHeader = [
      "Currency",
      "Ex. Rate",
      "CTC_Actual",
      "CTC_RD",
      "Cost_RD",
      "O365",
      "CrowdStrike",
      "Laptop",
      "H. Insurance",
      "GTL Insurance",
      "Total Cost",
      "Revenue",
      "GM",
      "Admin Cost",
      "Over Heads",
      "NM",
      "IT",
    ];
    const pushTrailing = (
      base: (string | number)[],
      row: Emp & Partial<Pick<ReportRow, "allocationPercent">>
    ): void => {
      const s = salaryCore[row.staffId] ?? {
        currency: "",
        monthlyAmount: 0,
      };
      const pct = aggregateByStaff
        ? computeRowTotal(row, report, mode)
        : pctFromMatrixCell(row.allocationPercent);
      const costRd = formatScaledCostRd(s.monthlyAmount, pct);
      base.push(
        s.currency,
        "",
        "",
        "",
        costRd,
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        ""
      );
    };

    const baseFixedHeaders = [
      "Entity",
      "Emp Type",
      "Employee ID",
      "Employment mode",
      "Staff name",
      "Status",
      "Date of joining",
      "Date of exit",
      "Line Manager",
    ];

    const baseFixedCells = (row: Emp): (string | number)[] => [
      row.entityName ?? "",
      row.employeeTypeName ?? "",
      row.employeeId ?? "",
      row.employmentModeName ?? "",
      row.staffName ?? "",
      row.staffStatusId ?? "",
      row.doj ? formatDate(row.doj, "DD-MM-YYYY") : "",
      row.lwd ? formatDate(row.lwd, "DD-MM-YYYY") : "",
      row.lineManagerName ?? "",
    ];

    let fields: string[];
    let dataRows: (string | number)[][];

    if (aggregateByStaff) {
      fields = [
        ...baseFixedHeaders,
        ...(mode === "client"
          ? report.clientColumnKeys
          : report.projectColumnDefs.map((p) => p.label)),
        "Total %",
        ...trailingHeader,
      ];
      dataRows = report.employees.map((row) => {
        const base = baseFixedCells(row);
        if (mode === "client") {
          const byClient = report.clientMatrix[row.staffId] ?? {};
          for (const k of report.clientColumnKeys) {
            const v = pctFromMatrixCell(byClient[k]);
            base.push(v > 0 ? Math.round(v * 100) / 100 : "");
          }
        } else {
          const byProject = report.projectMatrix[row.staffId] ?? {};
          for (const p of report.projectColumnDefs) {
            const v = pctFromMatrixCell(
              byProject[p.projectId] ?? byProject[String(p.projectId)]
            );
            base.push(v > 0 ? Math.round(v * 100) / 100 : "");
          }
        }
        base.push(computeRowTotal(row, report, mode));
        pushTrailing(base, row);
        return base;
      });
    } else if (compactDetailClientProject) {
      const flatRows = buildExpandedRows(report, mode);
      fields = [
        ...baseFixedHeaders,
        mode === "client" ? "Client" : "Project",
        "Allocation %",
        ...trailingHeader,
      ];
      dataRows = flatRows.map((row) => {
        const base = baseFixedCells(row);
        if (mode === "client") {
          base.push(row.allocationClient ?? "");
        } else {
          base.push(row.allocationProjectLabel ?? "");
        }
        base.push(
          row.allocationPercent != null && row.allocationPercent > 0
            ? Math.round(row.allocationPercent * 100) / 100
            : ""
        );
        pushTrailing(base, row);
        return base;
      });
    } else {
      const flatRows = buildExpandedRows(report, mode);
      fields = [
        ...baseFixedHeaders,
        ...(mode === "client"
          ? report.clientColumnKeys
          : report.projectColumnDefs.map((p) => p.label)),
        "Total %",
        ...trailingHeader,
      ];
      dataRows = flatRows.map((row) => {
        const base = baseFixedCells(row);
        if (mode === "client") {
          for (const k of report.clientColumnKeys) {
            const v =
              row.allocationClient === k
                ? pctFromMatrixCell(row.allocationPercent)
                : 0;
            base.push(v > 0 ? Math.round(v * 100) / 100 : "");
          }
        } else {
          for (const p of report.projectColumnDefs) {
            const v =
              row.allocationProjectId &&
              String(row.allocationProjectId) === String(p.projectId)
                ? pctFromMatrixCell(row.allocationPercent)
                : 0;
            base.push(v > 0 ? Math.round(v * 100) / 100 : "");
          }
        }
        const tot = pctFromMatrixCell(row.allocationPercent);
        base.push(tot > 0 ? Math.round(tot * 100) / 100 : "");
        pushTrailing(base, row);
        return base;
      });
    }

    const csv = Papa.unparse([fields, ...dataRows]);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `allocation-report_${mode}_${
        aggregateByStaff
          ? "matrix"
          : compactDetailClientProject
            ? "detail-compact"
            : "detail-wide"
      }_${ym}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [report, mode, month, salaryCore, aggregateByStaff, compactDetailClientProject]);

  const tableRows = useMemo((): ReportRow[] => {
    if (!report) return [];
    if (aggregateByStaff) {
      return report.employees.map((e) => ({ ...e, rowKey: e.staffId }));
    }
    return buildExpandedRows(report, mode);
  }, [report, mode, aggregateByStaff]);

  const fixedColumns: ColumnsType<ReportRow> = useMemo(
    () => [
      {
        title: "Entity",
        dataIndex: "entityName",
        key: "entityName",
        // fixed: "left",
        width: 120,
        ellipsis: true,
        render: (v: string | null | undefined) => v || "—",
      },
      {
        title: "Emp Type",
        dataIndex: "employeeTypeName",
        key: "employeeTypeName",
        // fixed: "left",
        width: 120,
        ellipsis: true,
        render: (v: string | null | undefined) => v || "—",
      },
      {
        title: "Employee ID",
        dataIndex: "employeeId",
        key: "employeeId",
        // fixed: "left",
        width: 120,
        ellipsis: true,
      },
      {
        title: "Employment mode",
        dataIndex: "employmentModeName",
        key: "employmentModeName",
        // fixed: "left",
        width: 130,
        ellipsis: true,
        render: (v: string | null | undefined) => v || "—",
      },
      {
        title: "Staff name",
        dataIndex: "staffName",
        key: "staffName",
        // fixed: "left",
        width: 180,
        ellipsis: true,
      },
      {
        title: "Status",
        dataIndex: "staffStatusId",
        key: "staffStatusId",
        // fixed: "left",
        width: 100,
      },
      {
        title: "Date of joining",
        key: "doj",
        // fixed: "left",
        width: 120,
        render: (_: unknown, r: Emp) =>
          r.doj ? formatDate(r.doj, "DD-MMM-YYYY") : "—",
      },
      {
        title: "Date of exit",
        key: "lwd",
        // fixed: "left",
        width: 120,
        render: (_: unknown, r: Emp) =>
          r.lwd ? formatDate(r.lwd, "DD-MMM-YYYY") : "—",
      },
      {
        title: "Line Manager",
        dataIndex: "lineManagerName",
        key: "lineManagerName",
        // fixed: "left",
        width: 160,
        ellipsis: true,
        render: (v: string | null | undefined) => v || "—",
      },
    ],
    []
  );

  const dynamicColumns: ColumnsType<ReportRow> = useMemo(() => {
    if (!report || !aggregateByStaff) return [];
    if (mode === "client") {
      return report.clientColumnKeys.map((clientName) => ({
        title: clientName,
        key: `c-${clientName}`,
        width: 130,
        align: "right" as const,
        ellipsis: true,
        render: (_: unknown, row: ReportRow) => {
          const v = pctFromMatrixCell(
            report.clientMatrix[row.staffId]?.[clientName]
          );
          return v > 0 ? `${v}%` : "";
        },
      }));
    }
    return report.projectColumnDefs.map((p) => ({
      title: p.label,
      key: `p-${p.projectId}`,
      width: 220,
      align: "right" as const,
      ellipsis: true,
      render: (_: unknown, row: ReportRow) => {
        const byProject = report.projectMatrix[row.staffId] ?? {};
        const v = pctFromMatrixCell(
          byProject[p.projectId] ?? byProject[String(p.projectId)]
        );
        return v > 0 ? `${v}%` : "";
      },
    }));
  }, [report, mode, aggregateByStaff]);

  const flatAllocationColumns: ColumnsType<ReportRow> = useMemo(() => {
    if (!report || aggregateByStaff || !compactDetailClientProject) return [];
    return [
      {
        title: mode === "client" ? "Client" : "Project",
        key: "allocationTarget",
        width: mode === "client" ? 200 : 260,
        ellipsis: true,
        render: (_: unknown, row: ReportRow) =>
          mode === "client"
            ? row.allocationClient || "—"
            : row.allocationProjectLabel || "—",
      },
      {
        title: "Allocation %",
        key: "allocationPercent",
        width: 110,
        align: "right" as const,
        render: (_: unknown, row: ReportRow) =>
          row.allocationPercent != null && row.allocationPercent > 0
            ? `${row.allocationPercent}%`
            : "",
      },
    ];
  }, [report, mode, aggregateByStaff, compactDetailClientProject]);

  /** Detail + wide: same client/project headers as matrix; one cell filled per row. */
  const dynamicColumnsWideDetail: ColumnsType<ReportRow> = useMemo(() => {
    if (!report || aggregateByStaff || compactDetailClientProject) return [];
    if (mode === "client") {
      return report.clientColumnKeys.map((clientName) => ({
        title: clientName,
        key: `cd-c-${clientName}`,
        width: 130,
        align: "right" as const,
        ellipsis: true,
        render: (_: unknown, row: ReportRow) => {
          if (!row.allocationClient || row.allocationClient !== clientName) {
            return "";
          }
          const p = pctFromMatrixCell(row.allocationPercent);
          return p > 0 ? `${p}%` : "";
        },
      }));
    }
    return report.projectColumnDefs.map((p) => ({
      title: p.label,
      key: `cd-p-${p.projectId}`,
      width: 220,
      align: "right" as const,
      ellipsis: true,
      render: (_: unknown, row: ReportRow) => {
        const pid = String(p.projectId);
        if (
          !row.allocationProjectId ||
          String(row.allocationProjectId) !== pid
        ) {
          return "";
        }
        const pr = pctFromMatrixCell(row.allocationPercent);
        return pr > 0 ? `${pr}%` : "";
      },
    }));
  }, [report, mode, aggregateByStaff, compactDetailClientProject]);

  const totalColumn: ColumnsType<ReportRow> = useMemo(() => {
    if (!report || !aggregateByStaff) return [];
    return [
      {
        title: "Total",
        key: "rowTotal",
        width: 100,
        align: "right" as const,
        render: (_: unknown, row: ReportRow) => {
          const t = computeRowTotal(row, report, mode);
          return `${t}%`;
        },
      },
    ];
  }, [report, mode, aggregateByStaff]);

  /** Detail + wide: total equals that row’s single allocation %. */
  const detailWideTotalColumn: ColumnsType<ReportRow> = useMemo(() => {
    if (!report || aggregateByStaff || compactDetailClientProject) return [];
    return [
      {
        title: "Total",
        key: "rowTotalDetailWide",
        width: 100,
        align: "right" as const,
        render: (_: unknown, row: ReportRow) => {
          const p = pctFromMatrixCell(row.allocationPercent);
          return p > 0 ? `${p}%` : "";
        },
      },
    ];
  }, [report, aggregateByStaff, compactDetailClientProject]);

  const trailingColumns: ColumnsType<ReportRow> = useMemo(() => {
    const blank = (title: string, key: string, width: number) => ({
      title,
      key,
      width,
      align: "right" as const,
      render: () => "",
    });
    return [
      {
        title: "Currency",
        key: "currency",
        width: 110,
        ellipsis: true,
        render: (_: unknown, row: ReportRow) =>
          salaryCore[row.staffId]?.currency ?? "",
      },
      blank("Ex. Rate", "exRate", 90),
      blank("CTC_Actual", "ctcActual", 100),
      blank("CTC_RD", "ctcRd", 90),
      {
        title: "Cost_RD",
        key: "costRd",
        width: 110,
        align: "right" as const,
        render: (_: unknown, row: ReportRow) => {
          if (!report) return "";
          const s = salaryCore[row.staffId];
          if (!s?.monthlyAmount) return "";
          const pct = aggregateByStaff
            ? computeRowTotal(row, report, mode)
            : pctFromMatrixCell(row.allocationPercent);
          return formatScaledCostRd(s.monthlyAmount, pct);
        },
      },
      blank("O365", "o365", 80),
      blank("CrowdStrike", "crowdStrike", 100),
      blank("Laptop", "laptop", 80),
      blank("H. Insurance", "hInsurance", 100),
      blank("GTL Insurance", "gtlInsurance", 110),
      blank("Total Cost", "totalCost", 100),
      blank("Revenue", "revenue", 90),
      blank("GM", "gm", 80),
      blank("Admin Cost", "adminCost", 100),
      blank("Over Heads", "overHeads", 100),
      blank("NM", "nm", 70),
      blank("IT", "it", 70),
    ];
  }, [salaryCore, report, mode, aggregateByStaff]);

  const columns = useMemo(
    () => {
      if (aggregateByStaff) {
        return [
          ...fixedColumns,
          ...dynamicColumns,
          ...totalColumn,
          ...trailingColumns,
        ];
      }
      if (compactDetailClientProject) {
        return [...fixedColumns, ...flatAllocationColumns, ...trailingColumns];
      }
      return [
        ...fixedColumns,
        ...dynamicColumnsWideDetail,
        ...detailWideTotalColumn,
        ...trailingColumns,
      ];
    },
    [
      fixedColumns,
      dynamicColumns,
      dynamicColumnsWideDetail,
      flatAllocationColumns,
      totalColumn,
      detailWideTotalColumn,
      trailingColumns,
      aggregateByStaff,
      compactDetailClientProject,
    ]
  );

  const tableScrollX = useMemo(() => {
    if (!report) return 2200;
    if (aggregateByStaff) {
      return Math.max(
        2200,
        1180 +
          dynamicColumns.length * (mode === "client" ? 130 : 220) +
          100 +
          1680
      );
    }
    if (compactDetailClientProject) {
      return Math.max(2000, 1180 + 1680);
    }
    return Math.max(
      2200,
      1180 +
        dynamicColumnsWideDetail.length * (mode === "client" ? 130 : 220) +
        100 +
        1680
    );
  }, [
    report,
    aggregateByStaff,
    compactDetailClientProject,
    dynamicColumns.length,
    dynamicColumnsWideDetail.length,
    mode,
  ]);

  if (!canView) {
    return (
      <div className="app-page">
        <Typography.Text type="danger">You do not have access.</Typography.Text>
      </div>
    );
  }
  return (
    <div className="app-page">
      <div className="app-page-header">
        <Row align="middle" justify="space-between" wrap>
          <Col flex="1" style={{ minWidth: 0 }}>
            <div className="app-page-title-wrap">
              <div className="app-page-icon">
                <ProjectOutlined />
              </div>
              <h1 className="app-page-title">Allocation Report</h1>
            </div>
            <p className="app-page-subtitle">
              Employee vs project allocation for a selected month (staff
              assignment). Choose month, then load.
            </p>
          </Col>
        </Row>
      </div>

      <div className="app-filter-bar" style={{ marginBottom: 16 }}>
        <Space wrap align="center" size="middle">
          <span className="app-filter-title" style={{ marginBottom: 0 }}>
            Month
          </span>
          <DatePicker
            picker="month"
            value={month}
            onChange={(v) => {
              setMonth(v);
              setReport(null);
              setSalaryCore({});
              setSalaryCoreReady(true);
              setCompactDetailClientProject(false);
            }}
            format="MMM YYYY"
            allowClear
            placeholder="Select month"
          />
          <Radio.Group
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            optionType="button"
            buttonStyle="solid"
          >
            <Radio.Button value="client">Client mode</Radio.Button>
            <Radio.Button value="project">Project mode</Radio.Button>
          </Radio.Group>
          <Button type="primary" onClick={loadReport} loading={loading}>
            Load report
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={downloadCsv}
            disabled={
              !report?.employees.length ||
              (report.employees.length > 0 && !salaryCoreReady)
            }
          >
            Download CSV
          </Button>
          <Space align="center">
            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
              One row per staff
            </Typography.Text>
            <Switch
              checked={aggregateByStaff}
              onChange={(c) => {
                setAggregateByStaff(c);
                if (c) setCompactDetailClientProject(false);
              }}
              disabled={
                !report?.employees.length ||
                (report.employees.length > 0 && !salaryCoreReady)
              }
            />
          </Space>
          {!aggregateByStaff && !!report?.employees.length && (
            <Space align="center">
              <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                Single Client/Project column
              </Typography.Text>
              <Switch
                checked={compactDetailClientProject}
                onChange={setCompactDetailClientProject}
                disabled={!salaryCoreReady}
              />
            </Space>
          )}
        </Space>
      </div>

      <div className="app-table-card">
        {!report ? (
          <Empty
            description="Select a month and click Load report to see data."
            style={{ padding: 48 }}
          />
        ) : report.employees.length === 0 ? (
          <Empty
            description="No allocation records for this month."
            style={{ padding: 48 }}
          />
        ) : (
          <Table<ReportRow>
            rowKey={(r) => r.rowKey}
            loading={
              loading ||
              (report.employees.length > 0 && !salaryCoreReady)
            }
            dataSource={tableRows}
            columns={columns}
            pagination={{ pageSize: 50, showSizeChanger: true }}
            scroll={{ x: tableScrollX }}
            size="small"
          />
        )}
      </div>
    </div>
  );
};

export default AllocationReportPage;
